/**
 * THE PARTY CALL REGISTER — `/countries/:cc/party-calls` (overnight v20 §D2).
 *
 * Every call logged against every customer in the country, newest first. The companion
 * to `PartyCallHistoryPanel`, which is this same server read narrowed to one party.
 *
 * ===========================================================================
 * WHAT THIS SCREEN IS NOT
 * ===========================================================================
 * It is NOT the inquiry follow-up timeline, and the two must not be confused by a reader
 * either — that confusion is the defect the §5 legacy verdict struck. A row here has NO
 * inquiry, no stage, no pipeline position; the column set says so by carrying none of
 * them. The trunk `Extra Call` badge on the inquiry Record page is a different thing and
 * is left exactly where it is.
 *
 * ===========================================================================
 * FILTERS FILTER ON THE SERVER
 * ===========================================================================
 * A chip writes a URL parameter and the next fetch carries it — never a client-side
 * filter over one fetched page, which would silently disagree with the pager. The
 * vocabulary is passed through as the URL holds it (the testing register's rule): the
 * server owns it and answers 400 naming it, where a silently-dropped bad parameter would
 * render the WHOLE register under a URL claiming to be filtered.
 *
 * ⚠ THERE IS NO COUNTRY CONTROL ON THIS SCREEN. The country comes from the route and the
 * session; a filter a user could change would be a second, weaker copy of the server
 * guard's decision.
 *
 * ===========================================================================
 * NOTHING HERE CAN BE MASKED
 * ===========================================================================
 * `party_calls` has no money column and no coordinate column, so no cell branches on
 * `'•••'` and no export of this table can leak one. The call NOTE is prose, gated at the
 * server by `parties.view`, and it renders in full — the same prose the events feed is
 * deliberately not told (owner ruling on `note_preview`).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { formatDateLong, formatTime } from '../inquiries/format';
import { FilterChip, DateRangeFilter } from '../field/bits';
import { PagerFooter } from '../renewals/PagerFooter';
import { OutcomePill } from './OutcomePill';
import { OUTCOME_LABEL, fetchPartyCalls, partyCallErrorText, partyCallKeys } from './api';
import type { PartyCallFilters, PartyCallRow } from './api';

const PER_PAGE = 50;

/**
 * The filter chips — the closed six, REORDERED FOR A READER. The entity puts
 * `'unspecified'` first for a MySQL enum-default reason that is not a reading order
 * (`party-calls.test.tsx` pins the SET equal to the API's); a chip row is not an INSERT
 * statement, so the two states that leave work OUTSTANDING lead.
 */
const OUTCOME_FILTERS: readonly { value: string | null; label: string }[] = [
  { value: null, label: 'All outcomes' },
  { value: 'callback_requested', label: OUTCOME_LABEL.callback_requested },
  { value: 'escalated', label: OUTCOME_LABEL.escalated },
  { value: 'connected', label: OUTCOME_LABEL.connected },
  { value: 'resolved', label: OUTCOME_LABEL.resolved },
  { value: 'no_answer', label: OUTCOME_LABEL.no_answer },
  { value: 'unspecified', label: OUTCOME_LABEL.unspecified },
];

export function PartyCallRegisterPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'parties.view');

  const partyId = params.get('party_id');
  const rawOutcome = params.get('outcome');
  const outcome = rawOutcome === null || rawOutcome === '' ? null : rawOutcome;
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo<PartyCallFilters>(
    () => ({
      partyId: partyId ?? undefined,
      outcome: outcome ?? undefined,
      dateFrom: from === '' ? undefined : from,
      dateTo: to === '' ? undefined : to,
      page,
      perPage: PER_PAGE,
    }),
    [partyId, outcome, from, to, page],
  );

  const list = useQuery({
    queryKey: partyCallKeys.register(countryCode, filters),
    queryFn: ({ signal }) => fetchPartyCalls(countryCode, filters, signal),
    enabled: canView,
  });

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The call register is not available to this account">
          Reading customer call logs in {countryCode} needs the parties view permission.
          Ask an administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const anyFilter = partyId !== null || outcome !== null || from !== '' || to !== '';

  const patchParams = (mutate: (patch: URLSearchParams) => void): void => {
    const patch = new URLSearchParams(params);
    mutate(patch);
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const setParamOrDrop = (key: string, next: string | null): void => {
    patchParams((patch) => {
      if (next === null) patch.delete(key);
      else patch.set(key, next);
    });
  };

  const setRange = (next: { from: string; to: string }): void => {
    patchParams((patch) => {
      if (next.from === '') patch.delete('from');
      else patch.set('from', next.from);
      if (next.to === '') patch.delete('to');
      else patch.set('to', next.to);
    });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Customer calls"
        subtitle={
          list.data === undefined
            ? undefined
            : `${String(total)} logged ${total === 1 ? 'call' : 'calls'} in ${countryCode}`
        }
      />

      <div className="flex flex-col gap-2">
        <div role="group" aria-label="Filter by outcome" className="flex flex-wrap items-center gap-2">
          {OUTCOME_FILTERS.map((chip) => (
            <FilterChip
              key={chip.label}
              label={chip.label}
              active={outcome === chip.value}
              onSelect={() => setParamOrDrop('outcome', chip.value)}
            />
          ))}
        </div>
        <DateRangeFilter from={from} to={to} onChange={setRange} />
        {partyId === null ? null : (
          <div className="flex flex-wrap items-center gap-2">
            {/* An applied party filter must always be nameable and clearable — an
                unnameable filter is how a reader concludes the register is empty. */}
            <span className="text-body13 text-fg-secondary">
              Showing one customer&apos;s calls
              {rows[0]?.party_name === undefined || rows[0]?.party_name === null
                ? ''
                : `: ${rows[0].party_name}`}
            </span>
            <button
              type="button"
              onClick={() => setParamOrDrop('party_id', null)}
              className="text-body13 font-medium text-indigo hover:underline"
            >
              Show every customer.
            </button>
          </div>
        )}
      </div>

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the call register" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The call register could not be loaded">
          <p>{partyCallErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)} logged {total === 1 ? 'call' : 'calls'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          {anyFilter ? (
            <EmptyState
              title="No call matches these filters."
              action={
                <button
                  type="button"
                  onClick={() =>
                    patchParams((patch) => {
                      for (const key of ['party_id', 'outcome', 'from', 'to']) patch.delete(key);
                    })
                  }
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Clear the filters.
                </button>
              }
            />
          ) : (
            <EmptyState title="No customer calls have been logged in this country yet." />
          )}
        </div>
      ) : (
        <CallTable countryCode={countryCode} rows={rows} />
      )}

      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

function CallTable({
  countryCode,
  rows,
}: {
  countryCode: string;
  rows: PartyCallRow[];
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full border-collapse text-body13">
        <caption className="sr-only">Customer calls logged in {countryCode}</caption>
        <thead>
          <tr className="border-b border-line text-left text-label text-fg-secondary">
            <th scope="col" className="px-4 py-2 font-medium">When</th>
            <th scope="col" className="px-4 py-2 font-medium">Customer</th>
            <th scope="col" className="px-4 py-2 font-medium">Outcome</th>
            <th scope="col" className="px-4 py-2 font-medium">Logged by</th>
            <th scope="col" className="px-4 py-2 font-medium">Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-line last:border-b-0 align-top">
              <td className="whitespace-nowrap px-4 py-2">
                <span className="tnum text-fg" title={row.logged_at}>
                  {formatDateLong(row.logged_at)}
                </span>
                <span className="tnum block text-fg-secondary">{formatTime(row.logged_at)}</span>
              </td>
              <td className="px-4 py-2">
                {/* Straight to that customer's own history panel — the register and the
                    panel are the same server read, so the link cannot show something
                    the row disagrees with. */}
                <Link
                  to={`/countries/${countryCode}/parties/${row.party_id}/calls`}
                  className="font-medium text-indigo hover:underline"
                >
                  {row.party_name ?? `Party ${row.party_id}`}
                </Link>
              </td>
              <td className="px-4 py-2">
                <OutcomePill outcome={row.outcome} />
              </td>
              <td className="whitespace-nowrap px-4 py-2 text-fg-secondary">
                {row.logged_by_name ?? `User ${row.logged_by}`}
              </td>
              <td className="px-4 py-2 text-fg">
                <span className="whitespace-pre-wrap">{row.note}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
